import React, { useState, useEffect } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

interface ErrorBoundaryError extends Error {
  message: string;
  stack?: string;
}

interface ErrorBoundaryProps {
  context?: string;
  onReset?: () => void;
  onError?: (error: ErrorBoundaryError, componentStack: string, context: string) => void;
  children: React.ReactNode;
}

const SimpleFallback = ({ onClose }: { onClose: () => void }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(128, 128, 128, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          textAlign: 'center',
          maxWidth: '400px',
          color: '#3a3737ff', // text color in hex
        }}
      >
        <h2 style={{ marginBottom: '1rem' }}>Study Not Found</h2>
        <p>Possible reasons :</p>
        <p>Your Internet Connection is Unstable. Or Offline.</p>
        <p>The requested study is not found as at now.</p>
        <p>However, The report is available in the Study List.</p>
        <div style={{ marginTop: '1.5rem' }}>
          <a
            href="/"
            style={{
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 'bold',
              marginBottom: '1rem',
              display: 'inline-block',
            }}
          >
            Return to Study List
          </a>
        </div>
        <button
          onClick={onClose}
          style={{
            marginTop: '1rem',
            backgroundColor: '#dc3545',
            color: 'white',
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ErrorBoundary = ({
  context = 'OHIF',
  onReset = () => {},
  onError = () => {},
  children,
}: ErrorBoundaryProps) => {
  const [error, setError] = useState<ErrorBoundaryError | null>(null);

  const onResetHandler = () => {
    setError(null);
    onReset();
  };

  useEffect(() => {
    let errorTimeout: NodeJS.Timeout;

    const handleError = (event: ErrorEvent) => {
      event.preventDefault();
      clearTimeout(errorTimeout);
      errorTimeout = setTimeout(() => {
        setError(event.error);
        onError(event.error, '', context);
      }, 100);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      event.preventDefault();
      clearTimeout(errorTimeout);
      errorTimeout = setTimeout(() => {
        setError(event.reason);
        onError(event.reason, '', context);
      }, 100);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      clearTimeout(errorTimeout);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [context, onError]);

  return (
    <ReactErrorBoundary
      fallbackRender={({ resetErrorBoundary }) => <SimpleFallback onClose={resetErrorBoundary} />}
      onReset={onResetHandler}
    >
      <>
        {children}
        {error && <SimpleFallback onClose={onResetHandler} />}
      </>
    </ReactErrorBoundary>
  );
};

export { ErrorBoundary };
